import { CreditCard, Lock } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

/** Stand-in for Stripe's Payment Element: the same fields, built from our inputs, collecting nothing. */
export function MockStripeCardForm({
  id,
  acceptsAch,
  offerSameCard,
  onSave,
}: {
  id: string
  /** Adds Stripe's US bank account tab beside the card. */
  acceptsAch?: boolean
  /** Reuses the card already given to another provider. */
  offerSameCard?: boolean
  onSave: () => void
}) {
  const cardFields = (
    <>
      {offerSameCard && (
        <Button type="button" variant="outline" className="self-start" onClick={onSave}>
          Use the same card
        </Button>
      )}
      <Field>
        <FieldLabel htmlFor={`${id}-number`}>Card number</FieldLabel>
        <div className="relative">
          <Input
            id={`${id}-number`}
            inputMode="numeric"
            autoComplete="off"
            placeholder="1234 1234 1234 1234"
            className="pr-10"
          />
          <CreditCard className="text-ink-400 pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2" />
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field>
          <FieldLabel htmlFor={`${id}-expiry`}>Expiration date</FieldLabel>
          <Input id={`${id}-expiry`} inputMode="numeric" autoComplete="off" placeholder="MM / YY" />
        </Field>
        <Field>
          <FieldLabel htmlFor={`${id}-cvc`}>Security code</FieldLabel>
          <Input id={`${id}-cvc`} inputMode="numeric" autoComplete="off" placeholder="CVC" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field>
          <FieldLabel htmlFor={`${id}-country`}>Country</FieldLabel>
          <Select defaultValue="US">
            <SelectTrigger id={`${id}-country`} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="US">United States</SelectItem>
              <SelectItem value="CA">Canada</SelectItem>
              <SelectItem value="MX">Mexico</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel htmlFor={`${id}-zip`}>ZIP code</FieldLabel>
          <Input id={`${id}-zip`} inputMode="numeric" autoComplete="off" placeholder="12345" />
        </Field>
      </div>
    </>
  )

  return (
    <form
      className="mt-3 flex max-w-md flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        onSave()
      }}
    >
      {acceptsAch ? (
        <Tabs defaultValue="card" className="gap-4">
          <TabsList>
            <TabsTrigger value="card">Card</TabsTrigger>
            <TabsTrigger value="ach">US bank account</TabsTrigger>
          </TabsList>
          <TabsContent value="card" className="flex flex-col gap-4">
            {cardFields}
          </TabsContent>
          <TabsContent value="ach" className="flex flex-col gap-4">
            <MockAchFields id={id} />
          </TabsContent>
        </Tabs>
      ) : (
        cardFields
      )}
      <div className="flex items-center justify-between gap-3">
        <span className="text-ink-500 flex items-center gap-1.5 text-sm">
          <Lock className="size-3.5" /> Secured by Stripe
        </span>
        <Button type="submit">Save</Button>
      </div>
    </form>
  )
}

/** Stripe's US bank account fields, mocked the same way. */
function MockAchFields({ id }: { id: string }) {
  return (
    <>
      <Field>
        <FieldLabel htmlFor={`${id}-holder`}>Account holder name</FieldLabel>
        <Input id={`${id}-holder`} autoComplete="off" placeholder="Full name or business name" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field>
          <FieldLabel htmlFor={`${id}-routing`}>Routing number</FieldLabel>
          <Input id={`${id}-routing`} inputMode="numeric" autoComplete="off" placeholder="110000000" />
        </Field>
        <Field>
          <FieldLabel htmlFor={`${id}-account`}>Account number</FieldLabel>
          <Input id={`${id}-account`} inputMode="numeric" autoComplete="off" placeholder="000123456789" />
        </Field>
      </div>
      <Field>
        <FieldLabel htmlFor={`${id}-account-type`}>Account type</FieldLabel>
        <Select defaultValue="checking">
          <SelectTrigger id={`${id}-account-type`} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="checking">Checking</SelectItem>
            <SelectItem value="savings">Savings</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </>
  )
}
